// Build-time configuration. Every value can be overridden with a VITE_* environment variable.
import { defineChain, type Address } from 'viem';

export interface Deployment {
  chainId: number;
  startBlock: number;
  day?: number; // seconds in one protocol day (86400 unless a fast testnet rehearsal)
  poolManager: Address;
  hook: Address;
  vault: Address;
  governor: Address;
  factory: Address;
  ipo: Address;
  router: Address;
}

const deployments = import.meta.glob<Deployment>('../../contracts/deployments/*.json', { eager: true, import: 'default' });

const env = import.meta.env;
const chainId = Number(env.VITE_CHAIN_ID ?? 31337);
const KNOWN: Record<number, { name: string; rpc: string; explorer: string; testnet: boolean }> = {
  4663: { name: 'Robinhood Chain', rpc: 'https://rpc.mainnet.chain.robinhood.com', explorer: 'https://robinhoodchain.blockscout.com', testnet: false },
  46630: { name: 'Robinhood Chain Testnet', rpc: 'https://rpc.testnet.chain.robinhood.com', explorer: 'https://explorer.testnet.chain.robinhood.com', testnet: true },
  31337: { name: 'Local chain', rpc: 'http://127.0.0.1:8545', explorer: '', testnet: true },
};
const known = KNOWN[chainId] ?? { name: 'Robinhood Chain Testnet', rpc: '', explorer: '', testnet: true };

export const config = {
  chainId,
  chainName: (env.VITE_CHAIN_NAME as string) ?? known.name,
  rpcUrl: (env.VITE_RPC_URL as string) ?? known.rpc,
  explorer: ((env.VITE_EXPLORER_URL as string) ?? known.explorer).replace(/\/$/, ''),
  testnet: env.VITE_TESTNET !== undefined ? env.VITE_TESTNET === 'true' : known.testnet,
  indexerUrl: ((env.VITE_INDEXER_URL as string) ?? 'http://127.0.0.1:8787').replace(/\/$/, ''),
  privyAppId: (env.VITE_PRIVY_APP_ID as string) ?? 'cmv8vbysj014o0fjv5qf5erpb',
  // Dev-only: a local private key wallet for automated browser tests. Never set in production builds.
  // In dev, `?pk=0x...` picks a different local test key per browser tab.
  e2eKey: env.DEV ? (new URLSearchParams(location.search).get('pk') ?? (env.VITE_E2E_PRIVATE_KEY as string) ?? '') : '',
  // deployments/<chainId>.json, or VITE_DEPLOYMENT=<file name> (e.g. 4663-rehearsal) for a test copy.
  deployment: deployments[`../../contracts/deployments/${(env.VITE_DEPLOYMENT as string) || chainId}.json`] ?? null,
};

if (!config.rpcUrl) throw new Error(`Set VITE_RPC_URL for chain ${chainId}`);

export const chain = defineChain({
  id: config.chainId,
  name: config.chainName,
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: [config.rpcUrl] } },
  blockExplorers: config.explorer ? { default: { name: 'Explorer', url: config.explorer } } : undefined,
  testnet: config.testnet,
});
